import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { playerDataSelector } from '../../../../common/store/selectors/playerData.selector';
import {
  setUserInteraction,
  updateAnchorDisabledByUser,
  changePendingAdStatus,
} from '../../store/controlsPlugin.actions';
import { useStyles } from '../../../../common/hooks/useStyles';
import { Icon } from '../../../../common/components/icon/icon';
import { CONTROLS_ICON_TYPES } from '../../icon/iconMapper';
import { getPlayPauseButtonStyleObject } from './playPauseButton.style';
import { ToolTip } from '../../../../common/components/toolTip/toolTip';
import { TOOLTIP_TITLES } from '../../controlsPlugin.constants';
import { TOOLTIP_POSITIONS } from '../../../../common/components/toolTip/toolTip.contants';
import { adsDataSelector } from '../../../../common/store/selectors/adsData.selector';
import { getPlayAndPauseId, getPlayAndPauseTooltipId } from '../../htmlElements/htmlElementsIdGetter';
import { isDesktopMode, isEmpty } from '../../../../common/utils/utils';
import { brandingDataSelector } from '../../../../common/store/selectors/brandingData.selector';
import { splitViewSelector } from '../../../../common/store/selectors/splitView.selector';
import { moreVideosSelector } from '../../../../common/store/selectors/moreVideos.selector';
import { useHoverRef } from '../../../../common/hooks/useHoverRef';
import { recirculationVideoDataSelector } from '../../../../common/store/selectors/recirculationVideoData.selector';
import { resumeVideo, pauseVideo, setShowControls } from '../../../../core/store/playerData/playerDataSlice';

const PLAY_ARIA_LABEL = 'Play';
const PAUSE_ARIA_LABEL = 'Pause';

export const PlayPauseButton: FunctionComponent = () => {
  const dispatch = useDispatch();
  const playerMode = useSelector(playerDataSelector.playerMode);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adStatus = useSelector(adsDataSelector.adStatus);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqueId);
  const brandingColor = useSelector(brandingDataSelector.brandingColor);
  const splitViewRatio = useSelector(splitViewSelector.splitViewRatio);
  const showGalleryView = useSelector(moreVideosSelector.showGalleryView);
  const numberOfScenes = useSelector(recirculationVideoDataSelector.numberOfScenes);

  const [iconWrapperRef, iconWrapperHovered] = useHoverRef();

  const isSplitView = !isEmpty(splitViewRatio);
  const desktopMode = isDesktopMode(playerMode);
  const showAdControls = isVastAd && adBreak;
  const isRecirculationControls = numberOfScenes !== 0 && !showAdControls;
  const showPlayButton = showAdControls ? adStatus === 'paused' : (videoTagStatus === 'paused' || videoTagStatus === 'seeking');

  const styles = useStyles(getPlayPauseButtonStyleObject, [playerSize, iconWrapperHovered, isSplitView, showGalleryView, isRecirculationControls]);
  const { playPauseButtonStyle, iconAndToolTipContainerStyle } = styles;

  const onPlayClicked = (): void => {
    if (showAdControls) {
      dispatch(changePendingAdStatus('playing'));
    } else {
      dispatch(resumeVideo());
      dispatch(setUserInteraction('play'));
    }
    dispatch(updateAnchorDisabledByUser(false));
  };

  const onPauseClicked = (): void => {
    if (showAdControls) {
      dispatch(changePendingAdStatus('paused'));
    } else {
      dispatch(pauseVideo());
      dispatch(setUserInteraction('pause'));
    }
  };

  const onPlayPauseFocus = (): void => {
    dispatch(setShowControls(true));
  };

  const PlayPauseIcon: FunctionComponent = () => (
    <div ref={iconWrapperRef}>
      <Icon
        id={getPlayAndPauseId(playerUniqueId)}
        icon={showPlayButton ? CONTROLS_ICON_TYPES.PLAY_FILL : CONTROLS_ICON_TYPES.PAUSE_FILL}
        style={playPauseButtonStyle}
        color={brandingColor}
        onClick={showPlayButton ? onPlayClicked : onPauseClicked}
        onControlFocus={onPlayPauseFocus}
        ariaLabel={showPlayButton ? PLAY_ARIA_LABEL : PAUSE_ARIA_LABEL}
      />
    </div>
  );

  return (
    desktopMode
      ? (
        <div style={iconAndToolTipContainerStyle}>
          <ToolTip
            id={getPlayAndPauseTooltipId(playerUniqueId)}
            content={showPlayButton ? TOOLTIP_TITLES.PLAY : TOOLTIP_TITLES.PAUSE}
            position={TOOLTIP_POSITIONS.TOP}
          >
            <PlayPauseIcon />
          </ToolTip>
        </div>
      ) : <PlayPauseIcon />
  );
};
